// Path tools (D3): Pen (P), Curvature (Shift+~), Pencil (N), Add / Delete / Convert anchor (+, −, Shift+C),
// Scissors (C), Knife (K). Paths they draw get the current appearance and are selected when finished.
import type { Tool, ToolHost, ToolPointer } from './tool';
import { useDesign } from '../state';
import { useDesignUi } from '../ui/designUi';
import { addNode, replaceWithPaths, setSubpaths, splitPathNode } from '../commands/commands';
import { newId } from '../model/ids';
import type { Anchor, Mat2D, Node, Subpath } from '../model/types';
import {
  curvatureSubpath,
  convertAnchor,
  deleteAnchor,
  insertAnchor,
  moveHandle,
  reverseSubpath,
  splitAtAnchor,
} from '../geometry/pathOps';
import { fitPoints } from '../geometry/fitCurve';
import { knifeCut, fromPaper } from '../geometry/paperBridge';
import { hitAnchor, hitSegment, toLocal, worldSubpaths } from './pathTarget';
import { geometry } from '../render/displayList';
import { drawAnchors } from './directTool';
import { activeLayerId } from './shapeTool';
import { toScreen } from '../render/viewMath';
import { inv, apply } from '../geometry/mat';
import { snapContext } from './snapContext';
import { cssVar } from '../../app/theme';

const TOL = 5;
const I: Mat2D = [1, 0, 0, 1, 0, 0];
const corner = (x: number, y: number): Anchor => ({ x, y, inX: x, inY: y, outX: x, outY: y, type: 'corner' });
let gesture = 0;

function newPath(sps: Subpath[]): Node {
  const appearance = structuredClone(useDesignUi.getState().current);
  // An open path drawn with no stroke would be invisible: give it the default stroke.
  if (!appearance.strokes.length && sps.some((s) => !s.closed))
    appearance.strokes = [
      {
        paint: { kind: 'solid', color: { rgb: [0, 0, 0], alpha: 1 } },
        width: 1,
        cap: 'butt',
        join: 'miter',
        miter: 10,
        dash: [],
        dashOffset: 0,
        align: 'center',
        opacity: 1,
        blend: 'normal',
      },
    ];
  return {
    id: newId(),
    kind: 'path',
    subpaths: sps,
    fillRule: 'nonzero',
    visible: true,
    locked: false,
    transform: [...I] as Mat2D,
    appearance,
  };
}

/** Shift: the point snapped to 0/45/90° from the previous anchor. */
function constrain(from: Anchor | undefined, x: number, y: number, on: boolean): [number, number] {
  if (!on || !from) return [x, y];
  const dx = x - from.x,
    dy = y - from.y;
  const a = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
  const len = Math.hypot(dx, dy) * Math.cos(Math.atan2(dy, dx) - a);
  return [from.x + len * Math.cos(a), from.y + len * Math.sin(a)];
}

/** Pen: click = corner, drag = smooth (symmetric handles; Alt moves only the out-handle), click the first
 *  anchor to close, click an open path's end to continue it, Enter / Esc to finish. Points are in world
 *  coordinates on new paths; a continued path is edited in its own coordinates. */
export class PenTool implements Tool {
  private id: string | null = null;
  private dragging = false;
  private closing = false;
  private hover: [number, number] | null = null;
  private merge = '';

  private sub(h: ToolHost): { sps: Subpath[]; world: Mat2D } | null {
    const it = this.id ? h.list.byId.get(this.id) : null;
    return it ? { sps: geometry(it.node).subpaths, world: it.world } : null;
  }

  down(p: ToolPointer, h: ToolHost) {
    this.merge = `pen-${++gesture}`;
    if (!p.shift) {
      const s = snapContext([], this.id ? [this.id] : []).point(p.wx, p.wy);
      p = { ...p, wx: s.x, wy: s.y };
    }
    const st = useDesign.getState();
    const tol = TOL * p.px;
    const cur = this.sub(h);
    if (!cur) {
      this.id = null;
      // Continue an open path from one of its ends?
      const an = hitAnchor(h.list, p.wx, p.wy, tol);
      if (an) {
        const sp = geometry(an.item.node).subpaths[an.sp];
        if (sp && !sp.closed && (an.i === 0 || an.i === sp.points.length - 1)) {
          // The continued subpath goes last (new anchors are appended to it), reversed if we start at its start.
          const sps = [...geometry(an.item.node).subpaths];
          const [mine] = sps.splice(an.sp, 1);
          st.run(setSubpaths(an.id, [...sps, an.i === 0 ? reverseSubpath(mine!) : mine!], this.merge));
          this.id = an.id;
          useDesignUi.getState().set({ selection: [an.item.topId] });
          return;
        }
      }
      const node = newPath([{ closed: false, points: [corner(p.wx, p.wy)] }]);
      st.run({ ...addNode(node, activeLayerId()), merge: this.merge });
      this.id = node.id;
      useDesignUi.getState().set({ selection: [node.id] });
      this.dragging = true;
      return;
    }
    const m = inv(cur.world);
    const sps = cur.sps.map((s) => ({ closed: s.closed, points: [...s.points] }));
    const sp = sps[sps.length - 1]!;
    const first = sp.points[0]!,
      last = sp.points[sp.points.length - 1]!;
    const [fx, fy] = apply(cur.world, first.x, first.y);
    if (sp.points.length > 1 && Math.abs(fx - p.wx) <= tol && Math.abs(fy - p.wy) <= tol) {
      sp.closed = true;
      st.run(setSubpaths(this.id!, sps, this.merge));
      this.closing = true;
      this.dragging = true;
      return;
    }
    const lastWorld = {
      ...last,
      ...(() => {
        const [x, y] = apply(cur.world, last.x, last.y);
        return { x, y };
      })(),
    };
    const [wx, wy] = constrain(lastWorld, p.wx, p.wy, p.shift);
    const [lx, ly] = apply(m, wx, wy);
    sp.points.push(corner(lx, ly));
    st.run(setSubpaths(this.id!, sps, this.merge));
    this.dragging = true;
  }

  move(p: ToolPointer, h: ToolHost, dragging: boolean) {
    h.setCursor('crosshair');
    this.hover = [p.wx, p.wy];
    const cur = this.sub(h);
    if (!dragging || !this.dragging || !cur) {
      h.redrawOverlay();
      return;
    }
    // Dragging out handles of the anchor just placed (or of the first anchor when closing).
    const [lx, ly] = apply(inv(cur.world), p.wx, p.wy);
    const sps = cur.sps.map((s) => ({ closed: s.closed, points: [...s.points] }));
    const sp = sps[sps.length - 1]!;
    const k = this.closing ? 0 : sp.points.length - 1;
    const a = sp.points[k]!;
    if (Math.hypot(lx - a.x, ly - a.y) < 1e-9) return;
    sp.points[k] = this.closing
      ? moveHandle({ ...a, type: 'symmetric' }, 'in', 2 * a.x - lx, 2 * a.y - ly, p.alt)
      : p.alt
        ? { ...a, outX: lx, outY: ly, type: 'corner' }
        : { ...a, outX: lx, outY: ly, inX: 2 * a.x - lx, inY: 2 * a.y - ly, type: 'symmetric' };
    useDesign.getState().run(setSubpaths(this.id!, sps, this.merge));
  }

  up() {
    this.dragging = false;
    useDesign.getState().endGesture();
    if (this.closing) this.finish();
    this.closing = false;
  }

  /** Enter / Esc / tool change: the path is done. */
  finish() {
    this.id = null;
    this.hover = null;
  }
  cancel() {
    this.finish();
  }

  overlay(g: CanvasRenderingContext2D, v: ReturnType<ToolHost['view']>, h: ToolHost) {
    const cur = this.sub(h);
    if (!cur || !this.id) return;
    drawAnchors(g, v, h.list, [], [this.id]);
    const sp = cur.sps[cur.sps.length - 1];
    if (!sp || sp.closed || !this.hover || this.dragging) return;
    const last = sp.points[sp.points.length - 1]!;
    const [ax, ay] = apply(cur.world, last.x, last.y),
      [ox, oy] = apply(cur.world, last.outX, last.outY);
    g.strokeStyle = cssVar('--sel');
    g.beginPath();
    g.moveTo(...toScreen(v, ax, ay));
    g.bezierCurveTo(...toScreen(v, ox, oy), ...toScreen(v, ...this.hover), ...toScreen(v, ...this.hover));
    g.stroke();
  }
}

/** Curvature: each click adds a point the curve passes through; Alt-click makes a corner; click the first
 *  point to close; Enter / Esc finish. */
export class CurvatureTool implements Tool {
  private id: string | null = null;
  private pts: [number, number][] = [];
  private corners = new Set<number>();
  private hover: [number, number] | null = null;

  down(p: ToolPointer, h: ToolHost) {
    const st = useDesign.getState();
    const tol = TOL * p.px;
    if (this.id && !h.list.byId.get(this.id)) this.finish();
    if (this.pts.length > 2 && Math.hypot(p.wx - this.pts[0]![0], p.wy - this.pts[0]![1]) <= tol) {
      st.run(setSubpaths(this.id!, [curvatureSubpath(this.pts, true, this.corners)]));
      this.finish();
      return;
    }
    if (p.alt) this.corners.add(this.pts.length);
    const sn = snapContext([], this.id ? [this.id] : []).point(p.wx, p.wy);
    this.pts.push([sn.x, sn.y]);
    const sp = curvatureSubpath(this.pts, false, this.corners);
    if (!this.id) {
      const node = newPath([sp]);
      st.run(addNode(node, activeLayerId()));
      this.id = node.id;
      useDesignUi.getState().set({ selection: [node.id] });
    } else st.run(setSubpaths(this.id, [sp]));
  }
  move(p: ToolPointer, h: ToolHost) {
    h.setCursor('crosshair');
    this.hover = [p.wx, p.wy];
    h.redrawOverlay();
  }
  up() {}
  finish() {
    this.id = null;
    this.pts = [];
    this.corners = new Set();
  }
  cancel() {
    this.finish();
  }
  overlay(g: CanvasRenderingContext2D, v: ReturnType<ToolHost['view']>, h: ToolHost) {
    if (!this.id) return;
    drawAnchors(g, v, h.list, [], [this.id]);
    if (this.hover && this.pts.length) {
      // Preview of the curve with the pointer as the next point.
      const sp = curvatureSubpath([...this.pts, this.hover], false, this.corners);
      g.strokeStyle = cssVar('--sel-soft');
      g.beginPath();
      g.moveTo(...toScreen(v, sp.points[0]!.x, sp.points[0]!.y));
      for (let i = 0; i < sp.points.length - 1; i++) {
        const a = sp.points[i]!,
          b = sp.points[i + 1]!;
        g.bezierCurveTo(
          ...toScreen(v, a.outX, a.outY),
          ...toScreen(v, b.inX, b.inY),
          ...toScreen(v, b.x, b.y),
        );
      }
      g.stroke();
    }
  }
}

/** Pencil: drag freehand; the stroke is fitted with Béziers (fidelity 2.5 screen px); ending near the start
 *  closes it. */
export class PencilTool implements Tool {
  private pts: [number, number][] = [];
  fidelity = 2.5;
  down(p: ToolPointer) {
    this.pts = [[p.wx, p.wy]];
  }
  move(p: ToolPointer, h: ToolHost, dragging: boolean) {
    h.setCursor('crosshair');
    if (!dragging || !this.pts.length) return;
    this.pts.push([p.wx, p.wy]);
    h.redrawOverlay();
  }
  up(p: ToolPointer, h: ToolHost) {
    const pts = this.pts;
    this.pts = [];
    if (pts.length < 2) return;
    const closed =
      pts.length > 8 &&
      Math.hypot(pts[0]![0] - pts[pts.length - 1]![0], pts[0]![1] - pts[pts.length - 1]![1]) <= 10 * p.px;
    const sp = fitPoints(pts, this.fidelity * p.px, closed);
    const node = newPath([sp]);
    useDesign.getState().run(addNode(node, activeLayerId()));
    useDesignUi.getState().set({ selection: [node.id] });
    h.redrawOverlay();
  }
  overlay(g: CanvasRenderingContext2D, v: ReturnType<ToolHost['view']>) {
    if (this.pts.length < 2) return;
    g.strokeStyle = cssVar('--sel');
    g.beginPath();
    this.pts.forEach(([x, y], i) => (i ? g.lineTo(...toScreen(v, x, y)) : g.moveTo(...toScreen(v, x, y))));
    g.stroke();
  }
}

/** Add (+) / Delete (−) / Convert (Shift+C) anchor point. */
export class AnchorTool implements Tool {
  private drag: { id: string; sp: number; i: number; start: Subpath[]; merge: string } | null = null;
  constructor(readonly mode: 'add' | 'delete' | 'convert') {}
  down(p: ToolPointer, h: ToolHost) {
    const st = useDesign.getState();
    const tol = TOL * p.px;
    if (this.mode === 'add') {
      const s = hitSegment(h.list, p.wx, p.wy, tol);
      if (!s) return;
      const local = geometry(s.item.node).subpaths;
      st.run(
        setSubpaths(
          s.item.id,
          local.map((sp, k) => (k === s.sp ? insertAnchor(sp, s.seg, s.t) : sp)),
        ),
      );
      return;
    }
    const a = hitAnchor(h.list, p.wx, p.wy, tol);
    if (!a) return;
    const local = geometry(a.item.node).subpaths;
    if (this.mode === 'delete') {
      const sp = local[a.sp]!;
      const next =
        sp.points.length <= 2
          ? local.filter((_, k) => k !== a.sp)
          : local.map((s, k) => (k === a.sp ? deleteAnchor(s, a.i) : s));
      if (next.length) st.run(setSubpaths(a.id, next));
      return;
    }
    // Convert: a click toggles corner / smooth; a drag pulls out symmetric handles.
    st.run(
      setSubpaths(
        a.id,
        local.map((s, k) =>
          k === a.sp
            ? { closed: s.closed, points: s.points.map((pt, i) => (i === a.i ? convertAnchor(s, a.i) : pt)) }
            : s,
        ),
      ),
    );
    this.drag = { id: a.id, sp: a.sp, i: a.i, start: structuredClone(local), merge: `convert-${++gesture}` };
  }
  move(p: ToolPointer, h: ToolHost, dragging: boolean) {
    h.setCursor(this.mode === 'add' ? 'copy' : 'pointer');
    if (!dragging || !this.drag) return;
    const it = h.list.byId.get(this.drag.id);
    if (!it) return;
    const [lx, ly] = apply(inv(it.world), p.wx, p.wy);
    const d = this.drag;
    const next = d.start.map((s, k) =>
      k !== d.sp
        ? s
        : {
            closed: s.closed,
            points: s.points.map((pt, i) =>
              i === d.i
                ? {
                    ...pt,
                    outX: lx,
                    outY: ly,
                    inX: 2 * pt.x - lx,
                    inY: 2 * pt.y - ly,
                    type: 'symmetric' as const,
                  }
                : pt,
            ),
          },
    );
    useDesign.getState().run(setSubpaths(d.id, next, d.merge));
  }
  up() {
    this.drag = null;
    useDesign.getState().endGesture();
  }
}

/** Scissors (C): click an anchor or a segment to cut the path there. */
export class ScissorsTool implements Tool {
  down(p: ToolPointer, h: ToolHost) {
    const st = useDesign.getState();
    const tol = TOL * p.px;
    let id: string, local: Subpath[], sp: number, i: number;
    const a = hitAnchor(h.list, p.wx, p.wy, tol);
    if (a) {
      id = a.id;
      local = geometry(a.item.node).subpaths;
      sp = a.sp;
      i = a.i;
    } else {
      const s = hitSegment(h.list, p.wx, p.wy, tol);
      if (!s) return;
      id = s.item.id;
      local = geometry(s.item.node).subpaths.map((x, k) => (k === s.sp ? insertAnchor(x, s.seg, s.t) : x));
      sp = s.sp;
      i = s.seg + 1;
    }
    const parts = splitAtAnchor(local[sp]!, i);
    const keep = [...local.slice(0, sp), parts[0]!, ...local.slice(sp + 1)];
    const extra = parts.slice(1).map((x) => [x]);
    const out: string[] = [];
    st.run(splitPathNode(id, keep, extra, out));
    useDesignUi.getState().set({ selection: [id, ...out].map((x) => h.list.byId.get(x)?.topId ?? x) });
  }
  move(_p: ToolPointer, h: ToolHost) {
    h.setCursor('crosshair');
  }
  up() {}
}

/** Knife (K): drag across closed shapes to slice them along the drag. */
export class KnifeTool implements Tool {
  private pts: [number, number][] = [];
  down(p: ToolPointer) {
    this.pts = [[p.wx, p.wy]];
  }
  move(p: ToolPointer, h: ToolHost, dragging: boolean) {
    h.setCursor('crosshair');
    if (!dragging || !this.pts.length) return;
    this.pts.push([p.wx, p.wy]);
    h.redrawOverlay();
  }
  up(_p: ToolPointer, h: ToolHost) {
    const line = this.pts;
    this.pts = [];
    if (line.length < 2) return;
    const xs = line.map((q) => q[0]),
      ys = line.map((q) => q[1]);
    const box = {
      minX: Math.min(...xs),
      minY: Math.min(...ys),
      maxX: Math.max(...xs),
      maxY: Math.max(...ys),
    };
    const st = useDesign.getState();
    const cut: string[] = [];
    for (const it of h.list.tree.search(box)) {
      if (it.hidden || it.locked || (it.node.kind !== 'path' && it.node.kind !== 'shape')) continue;
      const sps = worldSubpaths(it);
      if (!sps.every((s) => s.closed)) continue;
      const pieces = knifeCut(sps, line);
      if (!pieces) continue;
      const local = pieces.map((pc) => toLocal(it, fromPaper(pc)));
      const out: string[] = [];
      st.run(replaceWithPaths(it.id, local, out));
      cut.push(...out);
    }
    if (cut.length) useDesignUi.getState().set({ selection: cut });
    h.redrawOverlay();
  }
  overlay(g: CanvasRenderingContext2D, v: ReturnType<ToolHost['view']>) {
    if (this.pts.length < 2) return;
    g.strokeStyle = cssVar('--cut-line');
    g.beginPath();
    this.pts.forEach(([x, y], i) => (i ? g.lineTo(...toScreen(v, x, y)) : g.moveTo(...toScreen(v, x, y))));
    g.stroke();
  }
}

export { worldSubpaths };
